import {FornecedorPesquisa} from "@components/fornecedores/FornecedorPesquisa.js";
import {FormaPagamentoPesquisa} from "@components/formaPagamento/FormaPagamentoPesquisa.tsx";
import {SolicitacaoItemInformacaoNavLink} from '@components/solicitacoes/itens/SolicitacaoItemInformacao.tsx';
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {_SolicitacoesEndpoints} from "@localization/siteMap/endpoints/_SolicitacoesEnpoints.js";
import {IFornecedor} from "@models/fornecedores/fornecedor";
import type {IOrcamentoNovoDto} from "@models/solicitacoes/orcamentoDto.js";
import type {ISolicitacao, ISolicitacaoItem} from "@models/solicitacoes/solicitacoes.js";
import {
    Button,
    ButtonGroup,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import {ToMoney} from "@shared/MathHelp.ts";
import {IFormaPagamento} from "@models/formaPagamento/IFormaPagamento";
import {IntInputEntry, MoneyInputEntry} from "@shared/specialComponents/InputEntry.tsx";
import {ISolicitacaoOrcamento} from "@models/solicitacoes/orcamento.ts";
import {FileInputEntry, IFileData} from "@shared/specialComponents/FileInputEntry.tsx";

import {useApi} from "@contexts/hooks/api/useApi.ts";

interface OrcamentoEntryProps {
    solicitacao: ISolicitacao;
    item: ISolicitacaoItem;
    onCreate?: (o: ISolicitacaoOrcamento) => void;
}

export function OrcamentoEntry(props: OrcamentoEntryProps) {
    const {solicitacao, item, onCreate} = props;
    const {createAlertFromResponse, addPopUp, closePopUp} = useAlert();
    const {Get} = useApi()

    const [formaPagto, setFormaPagto] = useState<IFormaPagamento | undefined>(undefined);
    const [fornecedorSelected, setFornecedorSelected] = useState<IFornecedor | undefined>(undefined);
    const [file, setFile] = useState<IFileData | undefined>();

    const [orcamento, setOrcamento] = useState<IOrcamentoNovoDto>({
        idSolicitacao: solicitacao.id,
        idItem: item.id,
        idFornecedor: 0,
        valor: item.valorEstimado ?? 0,
        qtdAtendida: item.quantidade,
        idFormaPagamento: 0,
    });


    useEffect(() => {
        setOrcamento({...orcamento, idFornecedor: fornecedorSelected?.id || 0});
    }, [fornecedorSelected]);

    useEffect(() => {
        setOrcamento({...orcamento, idFormaPagamento: formaPagto?.id || 0});
    }, [formaPagto]);

    const diferenca = orcamento.valor - (item.valorEstimado ?? orcamento.valor);
    const valorEstimadoTotal = (item.valorEstimado ?? 0) * item.quantidade;
    const valorTotal = orcamento.valor * orcamento.qtdAtendida;
    const diferencaTotal = valorTotal - valorEstimadoTotal;

    const diffQtd = useMemo(() => {
        return orcamento.qtdAtendida - item.quantidade;
    }, [orcamento.qtdAtendida, item.quantidade]);

    const valorOk = useMemo(() => {
        return orcamento.valor != null && orcamento.valor > 0;
    }, [orcamento.valor]);

    const qtdOk = useMemo(() => {
        return orcamento.qtdAtendida != null && orcamento.qtdAtendida > 0;
    }, [orcamento.qtdAtendida]);

    const allOk = useMemo(() => {
        return valorOk && qtdOk && fornecedorSelected != null && formaPagto != null;
    }, [valorOk, qtdOk, fornecedorSelected, formaPagto]);

    useEffect(() => {
        setOrcamento({
            ...orcamento,
            anexo: file?.conteudoBase64
        });
    }, [file]);

    const sendHandler = () => {
        const send = () => Get(_SolicitacoesEndpoints.CreateOrcamento(orcamento)).then((x) => {
            createAlertFromResponse(x);
            if (x.status == 'Success') {
                if (onCreate != null)
                    onCreate(x.items);
            }
        });
        if (diffQtd != 0 || valorEstimadoTotal > 0 && diferenca > 0) {
            addPopUp({
                title: 'Confirmar diferenças', content: <Grid>
                    <Typography>Existem diferenças nos valores que o usuario solicitou, e o valor do orçamento, por
                        favor confirme ou volte e corrija</Typography>
                    <TableContainer>
                        <Table size={'small'}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>Valor esperado</TableCell>
                                    <TableCell>Valor informado</TableCell>
                                    <TableCell>Diferença</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orcamento.qtdAtendida != item.quantidade && <TableRow>
                                    <TableCell>Qtd unitaria diferente do solicitado</TableCell>
                                    <TableCell>{item.quantidade}</TableCell>
                                    <TableCell>{orcamento.qtdAtendida}</TableCell>
                                    <TableCell>{orcamento.qtdAtendida - item.quantidade}</TableCell>
                                </TableRow>}
                                {valorEstimadoTotal > 0 && diferencaTotal > 0 && <TableRow>
                                    <TableCell>Valor total diferente do esperado</TableCell>
                                    <TableCell>{valorEstimadoTotal}</TableCell>
                                    <TableCell>{valorTotal}</TableCell>
                                    <TableCell>{diferencaTotal} ({(((valorTotal - valorEstimadoTotal) / valorEstimadoTotal) * 100).toFixed(3)}%)</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Grid container size={12} mt={1} justifyContent={'flex-end'} component={ButtonGroup}>
                        <Button color={'warning'} onClick={send}>Está correto, desejo enviar o orçamento</Button>
                        <Button color={'info'} onClick={() => closePopUp()}>
                            Voltar e corrigir
                        </Button>
                    </Grid>

                </Grid>
            })
        } else {
            send().then()
        }
    };


    return (
        <Grid container>
            <Grid size={12} container alignItems={'center'} mb={1}>
                <Typography variant={'subtitle2'}> Novo orçamento para o item</Typography>
                {item && <SolicitacaoItemInformacaoNavLink value={item}/>}
            </Grid>
            <Grid size={12}>
                <FornecedorPesquisa required onItemSelected={setFornecedorSelected}/>
            </Grid>
            <TextField variant={'filled'}
                       fullWidth value={orcamento.descricao}
                       label={'Descrição'}
                       onChange={e => {
                           setOrcamento({...orcamento, descricao: e.target.value});
                       }}

            />
            <MoneyInputEntry label={`Valor unitário (estimativa do usuario: ${ToMoney(item.valorEstimado ?? 0, true)})`}
                             textFieldProps={{
                                 variant: 'filled',
                                 required: true,
                                 error: !valorOk,
                                 slotProps: {
                                     input: {
                                         endAdornment:
                                             <Tooltip title={'Valor total'}>
                                                 <Typography fontSize={'0.75rem'}
                                                             pr={0.5}
                                                             variant={'caption'}
                                                             color={diferenca > 0 ? 'red' : 'green'}>
                                                     {ToMoney(diferenca, true)}
                                                 </Typography>
                                             </Tooltip>,
                                     },
                                 },
                             }}
                             onChange={v => setOrcamento({...orcamento, valor: v ?? 0})}
                             value={orcamento.valor}/>

            <IntInputEntry onChange={v => setOrcamento({...orcamento, qtdAtendida: v})}
                           label={`Quantidade atendida (solicitado: ${item.quantidade}un)`}
                           textFieldProps={{
                               variant: 'filled',
                               required: true,
                               error: !qtdOk,
                               slotProps: {
                                   input: {
                                       endAdornment:
                                           <Typography variant={'caption'}
                                                       fontSize={'0.75rem'}>{ToMoney(valorTotal)}
                                               <Typography fontSize={'0.75rem'}
                                                           pl={0.5}
                                                           pr={0.5}
                                                           variant={'caption'}
                                                           color={diferencaTotal > 0 ? 'red' : 'green'}>
                                                   {ToMoney(diferencaTotal, true)}
                                               </Typography>
                                           </Typography>,
                                   },
                               },
                           }}
                           value={orcamento.qtdAtendida}/>
            <Grid size={12}>
                <FormaPagamentoPesquisa required onItemSelected={setFormaPagto}/>
            </Grid>
            <Grid size={12}>
                <Typography>Total: {ToMoney(valorTotal)}</Typography>
                <Typography>Diferença do
                    esperado
                    R$/Qtd: {ToMoney(diferencaTotal, false)} / {orcamento.qtdAtendida - item.quantidade}un</Typography>
            </Grid>
            <Grid size={12}>
                <FileInputEntry files={file && [file] || []} onChange={(f) => {
                    setFile(f && f[0] || undefined)
                }}/>
            </Grid>
            <Button fullWidth disabled={!allOk} variant={'contained'} onClick={sendHandler}>Salvar</Button>
        </Grid>
    );
}